import { useState, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Pie, Doughnut, Scatter, Radar } from 'react-chartjs-2';
import type { ChartData } from '@/types/chart';
import { CHART_COLORS, CHART_BORDER_COLORS } from '@/utils/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

/** Resolve a CSS variable to its computed value */
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

interface FleetChartProps {
  chart: ChartData;
  activeFilter?: string;
  typeOverride?: ChartData['type'];
  forceHorizontalBars?: boolean;
  forceVerticalBars?: boolean;
}

type MetricMeta = { label: string; unit: string | null };

function isOrdinalScatter(chart: ChartData): boolean {
  if (!chart.data.points?.length || !chart.data.labels?.length) {
    return false;
  }

  if (chart.data.labels.length !== chart.data.points.length) {
    return false;
  }

  return chart.data.points.every((point, index) => {
    if (typeof point?.x !== 'number') return false;
    const expectedA = index;
    const expectedB = index + 1;
    return Math.abs(point.x - expectedA) < 0.001 || Math.abs(point.x - expectedB) < 0.001;
  });
}

function hasNumericScatterPoints(chart: ChartData): boolean {
  return !!chart.data.points?.length && chart.data.points.every((point) => (
    typeof point?.x === 'number'
    && Number.isFinite(point.x)
    && typeof point?.y === 'number'
    && Number.isFinite(point.y)
  ));
}

function inferMetricMeta(chart: ChartData): MetricMeta {
  const metricCandidates = [
    chart.y_label,
    chart.dataset_label,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim());
  const titleCandidates = [chart.title]
    .filter(Boolean)
    .map((value) => String(value).trim());

  const metricText = metricCandidates.join(' ').toLowerCase();
  const titleText = titleCandidates.join(' ').toLowerCase();
  const text = [metricText, titleText].filter(Boolean).join(' ');
  const explicitLabel = metricCandidates.find((value) => value.length > 0)
    || titleCandidates.find((value) => value.length > 0)
    || 'Valor';
  const hasCountMetric = /\b(cantidad|conteo|count|total|numero|número|viajes|servicios|unidades)\b/.test(metricText);
  const hasAverageMetric = /\b(promedio|prom\.?|avg|media)\b/.test(metricText);

  if (hasCountMetric && !hasAverageMetric) {
    return { label: explicitLabel, unit: null };
  }

  if (text.includes('km/h') || text.includes('velocidad')) {
    return { label: explicitLabel.replace(/\s*\(?.*km\/h.*\)?/i, '').trim() || 'Velocidad', unit: 'km/h' };
  }
  if (text.includes('eta') || text.includes('minuto') || /\bmin\b/.test(text)) {
    return { label: explicitLabel.replace(/\s*\(?.*min.*\)?/i, '').trim() || 'ETA', unit: 'min' };
  }
  if (text.includes('distancia')) {
    return { label: explicitLabel.replace(/\s*\(?.*km.*\)?/i, '').trim() || 'Distancia', unit: 'km' };
  }
  if (text.includes('%') || text.includes('riesgo') || text.includes('porcentaje')) {
    return { label: explicitLabel.replace(/\s*\(?.*%.*\)?/i, '').trim() || 'Riesgo', unit: '%' };
  }

  return { label: explicitLabel, unit: null };
}

function humanizeAxisLabel(value: string | undefined, fallback: string): string {
  const cleaned = String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s*-\s*(?:flota completa|toda la flota).*$/i, '')
    .replace(/^dispersi[oó]n\s+(?:de|del|la|el)?\s*/i, '')
    .replace(/^gr[aá]fica\s+(?:de|del|la|el)?\s*/i, '')
    .replace(/\s*\((?:km\/h|km|%|min)\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return fallback;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function inferUnitFromText(value: string | undefined): string | null {
  const text = String(value || '').toLowerCase();
  if (!text) return null;
  if (text.includes('km/h') || text.includes('velocidad')) return 'km/h';
  if (text.includes('eta') || text.includes('minuto') || /\bmin\b/.test(text)) return 'min';
  if (text.includes('distancia') || text.includes('recorrido') || text.includes('kilomet')) return 'km';
  if (text.includes('%') || text.includes('riesgo') || text.includes('porcentaje')) return '%';
  return null;
}

function splitScatterTitle(title: string): { x?: string; y?: string } {
  let cleanTitle = String(title || '')
    .replace(/\s*-\s*(?:flota completa|toda la flota).*$/i, '')
    .trim();

  if (cleanTitle.includes(':')) {
    cleanTitle = cleanTitle.split(':').pop()?.trim() || cleanTitle;
  }

  const vsMatch = cleanTitle.match(/^(?:dispersi[oó]n\s+(?:de|del|la|el)?\s*)?(.+?)\s+vs\.?\s+(.+)$/i);
  if (vsMatch) {
    return { x: vsMatch[1], y: vsMatch[2] };
  }

  const contraMatch = cleanTitle.match(/^(?:dispersi[oó]n\s+(?:de|del|la|el)?\s*)?(.+?)\s+contra\s+(?:la|el|los|las)?\s*(.+)$/i);
  if (contraMatch) {
    return { x: contraMatch[2], y: contraMatch[1] };
  }

  return {};
}

function hasText(value: MetricMeta, pattern: RegExp): boolean {
  return pattern.test(`${value.label} ${value.unit || ''}`.toLowerCase());
}

function numericAxisMax(chart: ChartData, axis: 'x' | 'y'): number | null {
  const values = (chart.data.points || [])
    .map((point) => point?.[axis])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  return values.length ? Math.max(...values) : null;
}

function inferScatterAxisMeta(chart: ChartData, axis: 'x' | 'y'): MetricMeta {
  const explicitUnit = axis === 'x'
    ? (chart as ChartData & { x_unit?: string }).x_unit
    : (chart as ChartData & { y_unit?: string }).y_unit;
  const explicitLabel = axis === 'x' ? chart.x_label : chart.y_label;
  const titleParts = splitScatterTitle(chart.title);
  const axisText = explicitLabel || (axis === 'x' ? titleParts.x : titleParts.y);
  const label = humanizeAxisLabel(axisText, axis.toUpperCase());
  const unit = explicitUnit || inferUnitFromText(axisText) || inferUnitFromText(label);

  return { label, unit };
}

function normalizeScatterAxisMeta(chart: ChartData, meta: { x: MetricMeta; y: MetricMeta }) {
  if (chart.type !== 'scatter' || chart.x_label || chart.y_label || !chart.data.points?.length) {
    return meta;
  }

  const title = String(chart.title || '').toLowerCase();
  const mentionsRiskAndDistance = /riesgo|porcentaje/.test(title) && /distancia|recorrido|kil[oó]metro|km/.test(title);
  if (!mentionsRiskAndDistance) {
    return meta;
  }

  const xMax = numericAxisMax(chart, 'x');
  const yMax = numericAxisMax(chart, 'y');
  if (xMax === null || yMax === null) {
    return meta;
  }

  const xLooksRisk = hasText(meta.x, /%|riesgo|porcentaje/);
  const yLooksDistance = hasText(meta.y, /km|distancia|recorrido|kil[oó]metro/);

  if (xLooksRisk && yLooksDistance && xMax > 100 && yMax <= 100) {
    return {
      x: { label: 'Distancia restante', unit: 'km' },
      y: { label: 'Porcentaje de riesgo', unit: '%' },
    };
  }

  return meta;
}

function formatMetricValue(value: number, unit: string | null): string {
  const safeValue = unit === 'min' && value < 0 ? 0 : value;
  const rounded = Math.round(safeValue * 100) / 100;
  if (!unit) return `${rounded}`;
  if (unit === '%') return `${rounded}%`;
  return `${rounded} ${unit}`;
}

function formatAxisLabel(value: unknown, maxChars: number, maxLines: number): string | string[] {
  const label = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!label || maxLines <= 1 || label.length <= maxChars) {
    return label;
  }

  const tokens = label
    .split(/[\s/_-]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) {
    return label;
  }

  const lines: string[] = [];
  let current = '';

  tokens.forEach((token) => {
    const candidate = current ? `${current} ${token}` : token;
    if (candidate.length <= maxChars) {
      current = candidate;
      return;
    }
    if (current) {
      lines.push(current);
    }
    current = token;
  });

  if (current) {
    lines.push(current);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  return [
    ...lines.slice(0, Math.max(1, maxLines - 1)),
    lines.slice(Math.max(1, maxLines - 1)).join(' '),
  ];
}

function sanitizeMetricValue(value: number, unit: string | null): number {
  return unit === 'min' && value < 0 ? 0 : value;
}

function removeEmptyCategories(labels: string[] = [], values: number[] = []) {
  if (!labels.length || labels.length !== values.length) {
    return { labels, values };
  }

  const pairs = labels.map((label, index) => ({ label, value: values[index] }));
  const nonEmptyPairs = pairs.filter(({ value }) => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value !== 0
  ));

  if (!nonEmptyPairs.length) {
    return { labels, values };
  }

  return {
    labels: nonEmptyPairs.map(({ label }) => label),
    values: nonEmptyPairs.map(({ value }) => value),
  };
}

export default function FleetChart({
  chart,
  activeFilter,
  typeOverride,
  forceHorizontalBars = false,
  forceVerticalBars = false,
}: FleetChartProps) {
  const requestedChartType = typeOverride || chart.type;
  const normalizedChartType: Exclude<ChartData['type'], 'radial'> =
    requestedChartType === 'radial' ? 'radar' : requestedChartType;
  const metricMeta = useMemo(() => inferMetricMeta(chart), [chart]);
  const scatterAxisMeta = useMemo(() => normalizeScatterAxisMeta({ ...chart, type: normalizedChartType }, {
    x: inferScatterAxisMeta(chart, 'x'),
    y: inferScatterAxisMeta(chart, 'y'),
  }), [chart, normalizedChartType]);
  const renderType = useMemo(
    () => (normalizedChartType === 'scatter' && (!hasNumericScatterPoints(chart) || isOrdinalScatter(chart)) ? 'bar' : normalizedChartType),
    [chart, normalizedChartType]
  );

  const chartData = useMemo(() => {
    let labels = chart.data.labels;
    let values = chart.data.values;
    const points = chart.data.points;

    // Apply filter_groups if active
    if (activeFilter && chart.filter_groups) {
      const group =
        chart.filter_groups[activeFilter] ||
        Object.entries(chart.filter_groups).find(
          ([k]) => k.toLowerCase() === activeFilter.toLowerCase()
        )?.[1];

      if (group) {
        labels = group.labels;
        values = group.values;
      }
    }

    const shouldRemoveEmptyCategories = renderType !== 'line' && renderType !== 'scatter';

    if (renderType === 'scatter' && points) {
      const safePoints = metricMeta.unit === 'min'
        ? points.map((point) => ({
          ...point,
          y: typeof point?.y === 'number' && Number.isFinite(point.y)
            ? sanitizeMetricValue(point.y, metricMeta.unit)
            : point?.y,
        }))
        : points;

      return {
        datasets: [
          {
            label: chart.dataset_label || chart.title,
            data: safePoints,
            backgroundColor: CHART_COLORS[0],
            borderColor: CHART_BORDER_COLORS[0],
            pointRadius: 4,
          },
        ],
      };
    }

    if (chart.type === 'scatter' && renderType === 'bar' && points?.length) {
      values = points.map((point) => (typeof point?.y === 'number' && Number.isFinite(point.y) ? point.y : 0));
      if (!labels?.length) {
        labels = points.map((point, index) => String(point?.x ?? index + 1));
      }
    }

    if (shouldRemoveEmptyCategories) {
      const cleaned = removeEmptyCategories(labels, values);
      labels = cleaned.labels;
      values = cleaned.values;
    }

    values = values.map((value) => (
      typeof value === 'number' && Number.isFinite(value)
        ? sanitizeMetricValue(value, metricMeta.unit)
        : value
    ));

    const isRadar = renderType === 'radar';
    const isDenseBarDataset = renderType === 'bar' && labels.length > 18;

    return {
      labels,
      datasets: [
        {
          label: (activeFilter && chart.filter_groups ? activeFilter : undefined)
            || chart.dataset_label
            || chart.title,
          data: values,
          backgroundColor: isRadar
            ? CHART_COLORS.slice(0, values.length).map(c => c.replace('0.6)', '0.25)'))
            : CHART_COLORS.slice(0, values.length),
          borderColor: isRadar
            ? CHART_BORDER_COLORS.slice(0, values.length)
            : CHART_BORDER_COLORS.slice(0, values.length),
          borderWidth: isRadar ? 2 : 1,
          tension: 0.3,
          fill: isRadar ? true : renderType === 'line' ? false : undefined,
          maxBarThickness: isDenseBarDataset ? 18 : undefined,
          categoryPercentage: isDenseBarDataset ? 0.82 : undefined,
          barPercentage: isDenseBarDataset ? 0.92 : undefined,
          pointBackgroundColor: isRadar ? CHART_BORDER_COLORS.slice(0, values.length) : undefined,
          pointBorderColor: isRadar ? '#fff' : undefined,
          pointRadius: isRadar ? 5 : undefined,
        },
      ],
    };
  }, [chart, activeFilter, renderType, metricMeta.unit]);

  const options = useMemo(
    () => {
      // Resolve CSS variables at render time so Chart.js gets real color values
      const textSecondary = cssVar('--text-secondary');
      const textPrimary = cssVar('--text-primary');
      const textMuted = cssVar('--text-muted');
      const bgElevated = cssVar('--bg-elevated');
      const borderStrong = cssVar('--border-strong');
      const borderSubtle = cssVar('--border-subtle');

      const isRadar = renderType === 'radar';
      const isCircular = renderType === 'pie' || renderType === 'doughnut';
      const isBar = renderType === 'bar';
      const isLine = renderType === 'line';
      const isScatter = renderType === 'scatter';
      const labelCount = chartData.labels?.length ?? 0;
      const shouldUseHorizontalBars = isBar && !forceVerticalBars && (forceHorizontalBars || labelCount > 18);
      const isDenseCategoryChart = labelCount > 20 && !isCircular && !isRadar && !isScatter;
      const isVeryDenseCategoryChart = labelCount > 30 && !isCircular && !isRadar && !isScatter;

      const maxLabelsForDisplay = isLine ? 10 : isBar ? 18 : 12;
      const tooManyLabels = labelCount > maxLabelsForDisplay;
      const showDatalabels = chart.data.show_datalabels === true && !tooManyLabels && !isScatter && !isCircular && !isRadar;
      const maxXAxisChars = shouldUseHorizontalBars ? 34 : isVeryDenseCategoryChart ? 14 : isDenseCategoryChart ? 16 : 22;
      const maxXAxisLines = isVeryDenseCategoryChart ? 3 : isDenseCategoryChart ? 2 : 1;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: isScatter
          ? {
            mode: 'nearest' as const,
            intersect: true,
            axis: 'xy' as const,
          }
          : undefined,
        indexAxis: shouldUseHorizontalBars ? 'y' : 'x',
        layout: {
          padding: {
            top: isDenseCategoryChart ? 20 : 8,
            right: shouldUseHorizontalBars ? 18 : 8,
            bottom: shouldUseHorizontalBars ? 8 : isVeryDenseCategoryChart ? 82 : isDenseCategoryChart ? 64 : 0,
          },
        },
        plugins: {
          legend: {
            display: isCircular || isRadar,
            labels: {
              color: textSecondary,
              font: { family: 'Inter', size: 12 },
            },
          },
          tooltip: {
            backgroundColor: bgElevated,
            titleColor: textPrimary,
            bodyColor: textSecondary,
            borderColor: borderStrong,
            borderWidth: 1,
            mode: isScatter ? 'nearest' : undefined,
            intersect: isScatter ? true : undefined,
            callbacks: {
              title: (items: any[]) => {
                const item = items?.[0];
                const label = item?.label;
                if (Array.isArray(label)) return label.join(' ');
                return label ? String(label) : '';
              },
              label: (context: any) => {
                if (isScatter) {
                  const xValue = context?.parsed?.x;
                  const yValue = context?.parsed?.y;
                  if (typeof xValue === 'number' && typeof yValue === 'number') {
                    return [
                      `${scatterAxisMeta.x.label}: ${formatMetricValue(xValue, scatterAxisMeta.x.unit)}`,
                      `${scatterAxisMeta.y.label}: ${formatMetricValue(yValue, scatterAxisMeta.y.unit)}`,
                    ];
                  }
                }

                const rawValue = (shouldUseHorizontalBars ? context?.parsed?.x : context?.parsed?.y)
                  ?? context?.parsed?.r
                  ?? context?.parsed
                  ?? context?.raw;

                if (typeof rawValue !== 'number') {
                  return `${metricMeta.label}: ${rawValue}`;
                }

                return `${metricMeta.label}: ${formatMetricValue(rawValue, metricMeta.unit)}`;
              },
            },
          },
          datalabels: showDatalabels
            ? {
              color: textPrimary,
              font: { family: 'Inter', size: isDenseCategoryChart ? 10 : 12, weight: 'bold' as const },
              anchor: isRadar ? 'end' as const : 'end' as const,
              align: isRadar ? 'end' as const : 'top' as const,
              offset: isDenseCategoryChart ? 2 : 4,
              formatter: (value: any) => {
                if (typeof value !== 'number') return value;
                if (value <= 0) return '';
                return Math.round(value * 100) / 100;
              },
            }
            : { display: false },
        },
        scales: isRadar
          ? {
            r: {
              angleLines: { color: borderSubtle },
              grid: { color: borderSubtle },
              pointLabels: {
                color: textSecondary,
                font: { family: 'Inter', size: 12, weight: '600' as const },
              },
              ticks: {
                color: textMuted,
                backdropColor: 'transparent',
                font: { size: 10 },
              },
              beginAtZero: true,
            },
          }
          : isCircular
            ? undefined
            : shouldUseHorizontalBars
              ? {
                x: {
                  title: {
                    display: !!chart.y_label,
                    text: chart.y_label || '',
                    color: textMuted,
                  },
                  ticks: { color: textMuted, font: { size: 11 } },
                  beginAtZero: true,
                  grid: { color: borderSubtle },
                },
                y: {
                  title: {
                    display: !!chart.x_label,
                    text: chart.x_label || '',
                    color: textMuted,
                  },
                  ticks: {
                    color: textMuted,
                    autoSkip: false,
                    callback: function(this: any, value: any) {
                      const rawLabel = this.getLabelForValue ? this.getLabelForValue(value) : value;
                      return formatAxisLabel(rawLabel, maxXAxisChars, 2);
                    },
                    font: { size: labelCount > 30 ? 9 : 10 },
                    padding: 8,
                  },
                  grid: { display: false },
                },
              }
              : {
              x: {
                title: {
                  display: !!chart.x_label,
                  text: chart.x_label || '',
                  color: textMuted,
                },
                ticks: {
                  color: textMuted,
                  autoSkip: false,
                  callback: function(this: any, value: any) {
                    const rawLabel = this.getLabelForValue ? this.getLabelForValue(value) : value;
                    return formatAxisLabel(rawLabel, maxXAxisChars, maxXAxisLines);
                  },
                  font: { size: isVeryDenseCategoryChart ? 8 : isDenseCategoryChart ? 9 : 11 },
                  maxRotation: isDenseCategoryChart ? 60 : 35,
                  minRotation: isDenseCategoryChart ? 55 : 0,
                },
                grid: { color: borderSubtle },
              },
              y: {
                title: {
                  display: !!chart.y_label,
                  text: chart.y_label || '',
                  color: textMuted,
                },
                ticks: { color: textMuted, font: { size: 11 } },
                beginAtZero: metricMeta.unit === 'min' || isBar,
                grid: { color: borderSubtle },
              },
            },
      };
    },
    [chart, chartData.labels, metricMeta, renderType, scatterAxisMeta, forceHorizontalBars, forceVerticalBars]
  );

  const ChartComponent = {
    bar: Bar,
    line: Line,
    pie: Pie,
    doughnut: Doughnut,
    scatter: Scatter,
    radar: Radar,
  }[renderType] || Bar;

  return <ChartComponent data={chartData as any} options={options as any} />;
}
